/**
 * Room construction and the URL/connect policy.
 *
 * THE 2x DPR TRAP (study §3.1): LiveKit's adaptive stream sizes the forwarded
 * layer from the tile's *device* pixels. Left alone on a retina laptop, nine
 * 360 CSS-px tiles ask for 720p nine times — 15 Mbps to one viewer, and the box
 * collapses from ~274 concurrent participants to ~46. Two defences, both here:
 *   1. `pixelDensity: 1` — adaptive stream never multiplies by DPR.
 *   2. `livekitSubscriptions.applyPlan` sets explicit, role-clamped dimensions.
 * LiveKit takes the MINIMUM of the two, which is exactly the density policy.
 */
import type { QualityProfile } from '@cyphex/types';
import { Room, type RoomConnectOptions, type RoomOptions } from 'livekit-client';
import { publishDefaults } from './livekitCodec';
import { LIVEKIT_WS_URL } from './mediaEnv';

const CONNECT_TIMEOUT_MS = 15_000;
const JOIN_RETRIES = 3;

export function createLiveKitRoom(profile: QualityProfile): Room {
  const options: RoomOptions = {
    adaptiveStream: { pixelDensity: 1, pauseVideoInBackground: true },
    dynacast: true,
    // localMedia owns getUserMedia; unpublishing must not kill our blur canvas.
    stopLocalTrackOnUnpublish: false,
    disconnectOnPageLeave: true,
    publishDefaults: publishDefaults(profile),
  };
  return new Room(options);
}

/**
 * `autoSubscribe: false` is THE density lever — nothing is forwarded until
 * `applySubscriptions` asks for it, so an off-page publisher costs zero egress.
 */
export const ROOM_CONNECT_OPTIONS: RoomConnectOptions = {
  autoSubscribe: false,
  maxRetries: JOIN_RETRIES,
  peerConnectionTimeout: CONNECT_TIMEOUT_MS,
  websocketTimeout: CONNECT_TIMEOUT_MS,
};

/** The env override wins so a dev browser can reach a proxied SFU. */
export function resolveSfuUrl(fromApi: string): string {
  const url = LIVEKIT_WS_URL ?? fromApi.trim();
  if (url.length === 0) {
    throw new Error('No SFU URL: the join response carried none and VITE_LIVEKIT_WS_URL is unset.');
  }
  return url;
}
